import { createSelector } from "@reduxjs/toolkit";
import type { AppState } from "@redux/app-state";
import type { StudentSummaryVM, StudentHistoryEntryDTO } from "@shared-kernel/application/ports/student/student-repository";
import type { ActivityCardEntry } from "@shared-kernel/primary/shared/card-list/types";
import { formatEntry } from "../utils/activity-card-formatters";

/** Composite type preserving source data alongside display props */
export interface FormattedHistoryEntry {
  source: StudentHistoryEntryDTO;
  display: ActivityCardEntry;
}

type ExtendedStudentSummaryVM = Omit<StudentSummaryVM, "lastEntries"> & {
  lastEntries: FormattedHistoryEntry[];
};

const EMPTY_SUMMARY_DATA: ExtendedStudentSummaryVM = { lastEntries: [], afdasISWaitingPeriodEndDate: null, balance: 0 };
const EMPTY_SUMMARY = { data: EMPTY_SUMMARY_DATA };

export const selectStudentSummaryRetrieval = createSelector(
  [(state: AppState) => state.student.summaryRetrieval.data, (state: AppState) => state.student.summaryRetrieval.fetching],
  (summary, fetching) => {
    if (!summary) return { ...EMPTY_SUMMARY, fetching };

    const entries: FormattedHistoryEntry[] = [...summary.lastEntries]
      .map(entry => ({
        source: entry,
        display: formatEntry(entry),
      }))
      .sort((a, b) => new Date(b.source.creationDate).getTime() - new Date(a.source.creationDate).getTime());

    return {
      data: {
        ...summary,
        lastEntries: entries,
      },
      fetching,
    };
  },
);
